import React from 'react';
import { mockData } from '../mock';
import { Linkedin, Mail, Phone } from 'lucide-react';

const About = ({ currentLang }) => {
  const t = mockData.translations[currentLang];

  return (
    <div className="bg-black min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20">
        <div className="px-[7.6923%]">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-6xl font-bold text-white mb-6">
              About Nusantara <span className="text-[#00FFD1]">Capital.ai</span>
            </h1>
            <p className="text-xl text-white/85 leading-relaxed">
              Leading private equity group at the forefront of Southeast Asia's digital transformation
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 border-t border-white/25">
        <div className="px-[7.6923%]">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">Our Mission</h2>
              <p className="text-lg text-white/85 leading-relaxed mb-6">
                To identify, invest in, and accelerate the growth of innovative companies that leverage artificial intelligence, blockchain technology, and financial innovation to transform industries across Southeast Asia.
              </p>
              <p className="text-white/70 leading-relaxed">
                We believe that the convergence of AI, blockchain, and fintech represents the greatest opportunity for value creation in emerging markets, particularly in Indonesia and the broader Southeast Asian region.
              </p>
            </div>
            
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">Our Vision</h2>
              <p className="text-lg text-white/85 leading-relaxed mb-6">
                To become Southeast Asia's premier private equity firm specializing in next-generation technologies, creating sustainable value for our investors while driving meaningful innovation in underserved markets.
              </p>
              <div className="bg-white/5 border border-white/20 p-6">
                <h3 className="text-xl font-semibold text-[#00FFD1] mb-3">Global Reach, Local Expertise</h3>
                <p className="text-white/70">
                  Headquartered in Jakarta with global investment networks, we combine international best practices with deep local market knowledge.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 border-t border-white/25">
        <div className="px-[7.6923%]">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Leadership Team</h2>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              Experienced professionals with proven track records in technology investments
            </p>
          </div>
          
          {/* Founder */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-white/5 border border-white/20 p-8">
              <div className="flex flex-col lg:flex-row gap-8 items-center">
                <div className="w-48 h-48 rounded-lg overflow-hidden flex-shrink-0">
                  <img 
                    src={mockData.team.founder.image}
                    alt={mockData.team.founder.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 text-center lg:text-left">
                  <h3 className="text-3xl font-bold text-white mb-2">{mockData.team.founder.name}</h3>
                  <p className="text-xl text-[#00FFD1] mb-4">{mockData.team.founder.position}</p>
                  <p className="text-white/85 leading-relaxed mb-6">
                    {mockData.team.founder.bio}
                  </p>
                  <div className="flex justify-center lg:justify-start gap-4">
                    <a href={`mailto:${mockData.company.email}`} className="w-10 h-10 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center hover:bg-[#00FFD1] hover:text-black transition-all duration-300">
                      <Mail className="w-5 h-5" />
                    </a>
                    <a href={`https://wa.me/${mockData.company.whatsapp.replace(/[^0-9]/g, '')}`} className="w-10 h-10 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center hover:bg-[#00FFD1] hover:text-black transition-all duration-300">
                      <Phone className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Team Members */}
          <div className="grid lg:grid-cols-2 gap-8">
            {mockData.team.members.map((member, index) => {
              // Create initials from name for members without photos
              const initials = member.name.split(' ').map(n => n[0]).join('').toUpperCase();
              
              return (
                <div key={index} className="bg-white/5 border border-white/20 p-6">
                  <div className="flex flex-col items-center text-center">
                    {member.image ? (
                      // Display actual photo if available
                      <div className="w-32 h-32 rounded-lg overflow-hidden mb-4">
                        <img 
                          src={member.image}
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      // Display initials placeholder if no photo
                      <div className="w-32 h-32 rounded-lg bg-[#00FFD1]/20 flex items-center justify-center mb-4">
                        <div className="text-center">
                          <div className="w-20 h-20 mx-auto mb-2 bg-[#00FFD1]/30 rounded-full flex items-center justify-center">
                            <span className="text-2xl font-bold text-[#00FFD1]">{initials}</span>
                          </div>
                          <p className="text-white/60 text-xs">{member.position.split(' ')[0]}</p>
                        </div>
                      </div>
                    )}
                    <h3 className="text-xl font-bold text-white mb-1">{member.name}</h3>
                    <p className="text-[#00FFD1] mb-3">{member.position}</p>
                    <p className="text-white/70 text-sm leading-relaxed">
                      {member.bio}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 border-t border-white/25">
        <div className="px-[7.6923%]">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white text-center mb-16">Our Values</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                  <div className="w-8 h-8 bg-[#00FFD1] rounded-sm"></div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Innovation First</h3>
                <p className="text-white/70">
                  We seek out groundbreaking technologies that have the potential to reshape entire industries.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                  <div className="w-8 h-8 bg-[#00FFD1] rounded-sm"></div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Strategic Partnership</h3>
                <p className="text-white/70">
                  We work closely with portfolio companies to accelerate growth and maximize value creation.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                  <div className="w-8 h-8 bg-[#00FFD1] rounded-sm"></div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Market Impact</h3>
                <p className="text-white/70">
                  Our investments drive meaningful change in underserved markets across Southeast Asia.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;